import "./testDetailsScreen.scss";
import React, {useCallback, useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {CommonService} from "../../../shared/services";
import {IAPIResponseType} from "../../../shared/models/api.model";
import StatusCardComponent from "../../../shared/components/status-card/statusCardComponent";
import HeadingComponent from "../../../shared/components/heading/headingComponent";
import DetailsDataKeyValueComponent
    from "../../../shared/components/details-data-key-value-component/detailsDataKeyValueComponent";
import TabsWrapperComponent, {
    TabComponent,
    TabContentComponent,
    TabsComponent
} from "../../../shared/components/tabs/tabsComponent";
import TestDetailsViewEditComponent from "./test-details-view-edit/testDetailsViewEditComponent";
import TestAttachmentsViewEditComponent
    from "./test-attchments-view-edit/TestAttachmentsViewEditComponent";
import {useDispatch, useSelector} from "react-redux";
import {setCurrentNavParams, setSideMenuView} from "../../../store/actions/header.action";
import {ImageConfig} from "../../../constants";
import LottieFileGenarationComponent
    from "../../../shared/components/core/lottie-file-genaration/LottieFileGenarationComponent";
import TestConfigurationViewEditComponent
    from "./test-configuration-view-edit/TestConfigurationViewEditComponent";
import {IRootReducerState} from "../../../store/reducers";
import commonService from "../../../shared/services/common.service";
import AssignLabToTestViewEditComponent from "./assign-lab-to-test-view-edit/AssignLabToTestViewEditComponent";
import TestReportTypeViewEditComponent from "./test-report-type-view-edit/TestReportTypeViewEditComponent";
import TestIcdCodesViewEditComponent from "./test-icd-codes-view-edit/TestIcdCodesViewEditComponent";
import TestCptCodesViewEditComponent from "./test-cpt-codes-view-edit/TestCptCodesViewEditComponent";

const BASIC_DETAILS = 'basic_details';
const REPORT_TYPE = 'report_type';
const ATTACHMENTS = 'attachments';
const ICD_CODES = 'icd_codes';
const CPT_CODES = 'cpt_codes';
const CONFIGURATION = 'configuration';
const ASSIGNED_LABS = 'assigned_labs';

interface TestDetailsScreenProps {

}

const TestDetailsScreen = (props: TestDetailsScreenProps) => {

    const [activeTab, setActiveTab] = useState<"basic_details" | "attachments" | "configuration" | "assigned_labs" | "report_type">(BASIC_DETAILS);
    const navigate = useNavigate();
    const params = useParams<{
        testID: string | undefined
    }>();
    const {testID} = params;
    const [testDetails, setTestDetails] = useState<any>(undefined);
    const [isTestDetailsLoading, setIsTestDetailsLoading] = useState<boolean>(false);
    const [isTestDetailsLoaded, setIsTestDetailsLoaded] = useState<boolean>(false);
    const [isTestDetailsLoadingFailed, setIsTestDetailsLoadingFailed] = useState<boolean>(false);
    const [testDetailsMode, setTestDetailsMode] = useState<"view" | "edit">("view");
    const dispatch = useDispatch();
    const {currentUser} = useSelector((state: IRootReducerState) => state.account);
    const [canEdit, setCanEdit] = useState<boolean>(false);

    useEffect(() => {
        setCanEdit(commonService.checkAccess('Test',
            'testEdit', currentUser?.userAccessModules))
    }, [currentUser]);

    useEffect(() => {
        if (testID) {
            dispatch(setCurrentNavParams("Back", "Test", CommonService._routeConfig.TestListRoute()));
            dispatch(setSideMenuView('compact'));
        }
    }, [testID, dispatch, navigate]);

    const getTestDetails = useCallback(() => {
        let testDetails: any = {};
        setIsTestDetailsLoading(true);
        setIsTestDetailsLoaded(false);
        setIsTestDetailsLoadingFailed(false);
        CommonService._test.getTestDetails(testID)
            .then((response: IAPIResponseType<any>) => {
                if (response?.data) {
                    testDetails = response.data;
                }
                setTestDetails(testDetails);
                setIsTestDetailsLoading(false);
                setIsTestDetailsLoaded(true);
                setIsTestDetailsLoadingFailed(false);
            })
            .catch((error: any) => {
                setTestDetails(testDetails);
                setIsTestDetailsLoading(false);
                setIsTestDetailsLoaded(false);
                setIsTestDetailsLoadingFailed(true);
            });
    }, [testID]);

    const handleDetailsUpdate = useCallback((testDetails: any, redirectTabId?: "basic_details" | "attachments" | "configuration" | "assigned_labs" | "report_type") => {
        setTestDetails(() => {
            return {
                ...testDetails
            }
        });
        if (redirectTabId) {
            setActiveTab(redirectTabId)
        }
    }, []);

    useEffect(() => {
        if (testID) {
            getTestDetails();
        }
    }, [testID, getTestDetails]);

    const handleActiveTabChange = useCallback((value: any) => {
        if (testDetailsMode === 'edit') {
            CommonService.openConfirmationDialog(
                {
                    confirmationTitle: 'Please confirm!',
                    confirmationSubTitle: 'Are you sure you want to discard the recent changes?',
                    disableEscKeyClose: false,
                    disableBackdropClose: true,
                    imagePath: `${ImageConfig.ConfirmationImage}`,
                    direction: "up",
                    yes: {
                        text: "Discard",
                        color: "primary"
                    },
                    no: {
                        text: "Cancel",
                        color: "primary"
                    }
                }).then(() => {
                setActiveTab(value);
            }).catch(() => {

            })
        } else {
            setActiveTab(value);
        }
    }, [testDetailsMode])

    return (
        <div className="test-details-screen details-screen screen">
            {
                isTestDetailsLoading && <div className="h-v-center">
                    <LottieFileGenarationComponent
                        loop={true}
                        autoplay={true}
                        animationData={ImageConfig.LoadingLottieFile}/>
                </div>
            }
            {
                isTestDetailsLoadingFailed && <>
                    <StatusCardComponent title={"Test details failed to load"}/>
                </>
            }
            {
                isTestDetailsLoaded && <>
                    <div className={'details-module-title'}>Test Details</div>
                    <div className="basic-details-wrapper">
                        <div className="details-screen-header">
                            <HeadingComponent title={testDetails?.name}
                            />
                        </div>

                        <div className="details-screen-sub-header">
                            <DetailsDataKeyValueComponent
                                title={"Test Id:"}
                                isHorizontal={true}> {testDetails?.id} </DetailsDataKeyValueComponent>

                            <DetailsDataKeyValueComponent
                                title={"Added by:"}
                                isHorizontal={true}> {testDetails?.createdByDetails?.firstName} {testDetails?.createdByDetails?.lastName} </DetailsDataKeyValueComponent>
                            <DetailsDataKeyValueComponent
                                title={"Created on:"}
                                isHorizontal={true}>
                                <>
                                    {commonService.formatDate(testDetails?.createdAt)}
                                    &nbsp;|&nbsp;
                                    {commonService.formatTime(testDetails?.createdAt)}
                                </>
                            </DetailsDataKeyValueComponent>
                        </div>

                    </div>
                    <div className="clear-fix"/>
                    <div className="details-screen-body">
                        <TabsWrapperComponent>
                            <TabsComponent
                                value={activeTab}
                                allowScrollButtonsMobile={false}
                                onUpdate={(e: any, v: any) => {
                                    handleActiveTabChange(v)
                                }}
                                variant={"fullWidth"}
                            >
                                <TabComponent label="BASIC DETAILS" value={BASIC_DETAILS}/>
                                <TabComponent label="REPORT TYPE" value={REPORT_TYPE}/>
                                <TabComponent label={"ICD CODES"} value={ICD_CODES}/>
                                <TabComponent label={"CPT CODES"} value={CPT_CODES}/>
                                <TabComponent label={"CONFIGURATION"} value={CONFIGURATION}/>
                                <TabComponent label={"ASSIGNED LABS"} value={ASSIGNED_LABS}/>
                                <TabComponent label="ATTACHMENTS" value={ATTACHMENTS}/>

                            </TabsComponent>

                            <TabContentComponent selectedTab={activeTab} value={BASIC_DETAILS}>
                                <TestDetailsViewEditComponent
                                    testDetails={testDetails}
                                    canEdit={canEdit}
                                    onUpdateInformation={handleDetailsUpdate}
                                    onModeChange={(mode: any) => {
                                        setTestDetailsMode(mode);
                                    }
                                    }
                                />
                            </TabContentComponent>
                            <TabContentComponent selectedTab={activeTab} value={REPORT_TYPE}>
                                <TestReportTypeViewEditComponent
                                    testDetails={testDetails}
                                    canEdit={canEdit}
                                    onUpdateInformation={handleDetailsUpdate}
                                    onModeChange={(mode: any) => {
                                        setTestDetailsMode(mode);
                                    }
                                    }
                                />
                            </TabContentComponent>
                            <TabContentComponent selectedTab={activeTab} value={ICD_CODES}>
                                <TestIcdCodesViewEditComponent testDetails={testDetails}
                                                               onUpdateInformation={handleDetailsUpdate}
                                                               onModeChange={(mode: any) => {
                                                                   setTestDetailsMode(mode);
                                                               }
                                                               }
                                                               canEdit={canEdit}
                                />
                            </TabContentComponent>
                            <TabContentComponent selectedTab={activeTab} value={CPT_CODES}>
                                <TestCptCodesViewEditComponent testDetails={testDetails}
                                                               onUpdateInformation={handleDetailsUpdate}
                                                               onModeChange={(mode: any) => {
                                                                   setTestDetailsMode(mode);
                                                               }
                                                               }
                                                               canEdit={canEdit}
                                />
                            </TabContentComponent>
                            <TabContentComponent selectedTab={activeTab} value={CONFIGURATION}>
                                <TestConfigurationViewEditComponent
                                    testDetails={testDetails}
                                    canEdit={canEdit}
                                    onUpdateInformation={handleDetailsUpdate}
                                    onModeChange={(mode: any) => {
                                        setTestDetailsMode(mode);
                                    }
                                    }
                                />
                            </TabContentComponent>
                            <TabContentComponent selectedTab={activeTab} value={ASSIGNED_LABS}>
                                <AssignLabToTestViewEditComponent
                                    testDetails={testDetails}
                                    canEdit={canEdit}
                                    onUpdateInformation={handleDetailsUpdate}
                                    onModeChange={(mode: any) => {
                                        setTestDetailsMode(mode);
                                    }
                                    }
                                />
                            </TabContentComponent>
                            <TabContentComponent selectedTab={activeTab} value={ATTACHMENTS}>
                                <TestAttachmentsViewEditComponent
                                    testDetails={testDetails}
                                    canEdit={canEdit}
                                    onModeChange={(mode: any) => {
                                        setTestDetailsMode(mode);
                                    }
                                    }
                                />
                            </TabContentComponent>
                        </TabsWrapperComponent>
                    </div>
                </>
            }
        </div>
    );

};

export default TestDetailsScreen;
